import { useState } from 'react';
import { Icon, tokens } from '@/design';
import { auditClient } from '@/app/audit/auditDataClient';

export interface EvidencePackPanelProps {
  isOpen: boolean;
  onClose: () => void;
  rowId?: string;
  userEmail?: string;
}

const INCLUSION_KEYS = ['layers', 'scenarios', 'time range', 'annotations', 'audit rows'];

export function EvidencePackPanel({
  isOpen,
  onClose,
  rowId,
  userEmail = 'user@example.com',
}: EvidencePackPanelProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set(INCLUSION_KEYS));
  const [message, setMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  const generate = async () => {
    const result = await auditClient.downloadEvidencePack(rowId);
    setMessage(
      result.kind === 'backend-not-deployed'
        ? 'Evidence-pack backend is not deployed yet.'
        : result.kind === 'error'
          ? result.message
          : 'Evidence pack requested.',
    );
  };

  return (
    <section
      role="dialog"
      aria-modal="true"
      aria-labelledby="evidence-pack-heading"
      data-gvw-id="GVW-132"
      style={{
        position: 'fixed',
        inset: tokens.space[5],
        zIndex: tokens.zIndex.modal,
        background: tokens.color.surface[1],
        color: tokens.color.ink.primary,
        border: tokens.border.thin,
        borderRadius: tokens.radius.lg,
        boxShadow: tokens.elevation[3],
        padding: tokens.space[5],
        fontFamily: tokens.font.family.ui,
      }}
    >
      <h2 id="evidence-pack-heading">
        <Icon name="summarize" size={20} decorative /> Evidence Pack
      </h2>
      <p style={{ color: tokens.color.ink.secondary }}>
        Bundle evidence for {rowId ? `row ${rowId}` : 'the current session'} as {userEmail}.
      </p>
      <fieldset style={{ border: tokens.border.thin, borderRadius: tokens.radius.md, padding: tokens.space[3] }}>
        <legend>Include</legend>
        {INCLUSION_KEYS.map((key) => (
          <label key={key} style={{ display: 'block', marginBlock: tokens.space[1] }}>
            <input
              type="checkbox"
              checked={selected.has(key)}
              onChange={(event) => {
                setSelected((current) => {
                  const next = new Set(current);
                  if (event.currentTarget.checked) next.add(key);
                  else next.delete(key);
                  return next;
                });
              }}
            />{' '}
            {key}
          </label>
        ))}
      </fieldset>
      <div style={{ display: 'flex', gap: tokens.space[2], marginTop: tokens.space[4] }}>
        <button type="button" onClick={onClose} style={buttonStyle('secondary')}>
          Cancel
        </button>
        <button type="button" onClick={generate} disabled={selected.size === 0} style={buttonStyle('primary')}>
          Generate evidence pack
        </button>
      </div>
      {message ? <p role="status">{message}</p> : null}
    </section>
  );
}

function buttonStyle(kind: 'primary' | 'secondary') {
  return {
    minHeight: tokens.density.touchMin,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: kind === 'primary' ? tokens.color.accent.primary : tokens.color.surface[2],
    color: kind === 'primary' ? tokens.color.accent.on : tokens.color.ink.primary,
    paddingInline: tokens.space[3],
    cursor: 'pointer',
  };
}
